/**
 * Custom confirmation pages: fills the registrant merge tags in the page's own HTML once the
 * registration is known. The server already filled the webinar tags and turned block tags into
 * `[data-ffs-block]` placeholders (mounted separately, see mount.tsx); what is left here are the
 * `{{first_name}}`-style tags in text and attributes, which only the browser can fill because the
 * join token never reaches the server with the page request.
 */
import type { RegistrationDetails } from '@ffs/shared';
import { getRegistration } from './api';

export type TagValues = Record<string, string>;

const TAG_RE = /\{\{\s*([a-z_]+)\s*\}\}/g;

/** The text tags and their values; empty strings when there is no registration to show. */
export function tagValues(
  details: RegistrationDetails | null,
  base: string,
  token: string | null,
): TagValues {
  const t = token ? encodeURIComponent(token) : null;
  return {
    first_name: details?.contact.first_name ?? '',
    last_name: details?.contact.last_name ?? '',
    email: details?.contact.email ?? '',
    session_start_local: details?.session_start_label ?? '',
    session_start_iso: details?.session_start_iso ?? '',
    // Until the details arrive (or if they cannot be loaded) the token alone gives the join link
    // and the calendar file, the same as the standard confirmation.
    join_url: details?.join_url ?? (t ? `${base}/j/${t}` : ''),
    reschedule_url: details?.reschedule_url ?? (t ? `${base}/j/${t}/reschedule` : ''),
    calendar_google_url: details?.calendar.google ?? '',
    calendar_outlook_url: details?.calendar.outlook ?? '',
    calendar_ics_url: details?.calendar.ics ?? (t ? `${base}/cal/${t}.ics` : ''),
    webinar_title: details?.webinar.title ?? '',
    presenter_name: details?.webinar.presenter_name ?? '',
    presenter_photo_url: details?.webinar.presenter_photo_url ?? '',
  };
}

/** Replace every known tag in a string; unknown `{{…}}` are left alone. */
export function fillString(s: string, values: TagValues): string {
  if (!s.includes('{{')) return s;
  return s.replace(TAG_RE, (whole, name: string) => (name in values ? values[name]! : whole));
}

const SKIP = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'NOSCRIPT']);

/**
 * Walk the page and fill tags in text nodes and attribute values. Scripts are left as written:
 * they read the registration from `window.FFS.registration` or the `ffs:confirmation` event.
 */
export function fillTree(root: Element, values: TagValues): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as Element;
        if (SKIP.has(el.tagName) || el.hasAttribute('data-ffs-block'))
          return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
      return node.nodeValue?.includes('{{') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
    },
  });
  const texts: Text[] = [];
  const els: Element[] = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (n.nodeType === Node.TEXT_NODE) texts.push(n as Text);
    else els.push(n as Element);
  }
  for (const el of els) {
    for (const attr of Array.from(el.attributes)) {
      if (attr.value.includes('{{')) el.setAttribute(attr.name, fillString(attr.value, values));
    }
    // An image whose address is empty (a tag with nothing behind it, such as a webinar without a
    // presenter photo) would show a broken-image icon; hiding it is what the author would want.
    if (el.tagName === 'IMG' && el.getAttribute('src') === '') el.setAttribute('hidden', '');
  }
  for (const text of texts) text.nodeValue = fillString(text.nodeValue ?? '', values);
}

declare global {
  interface Window {
    FFS?: { registration?: RegistrationDetails | null };
  }
}

/**
 * A `[data-ffs-page="<form public id>"]` root with an optional `data-ffs-registered` token.
 * Fills the tags, then shows the page (the inline boot script hid it so raw tags never flash).
 */
export async function fillPage(root: HTMLElement, base: string): Promise<void> {
  const token = root.getAttribute('data-ffs-registered')?.trim() || null;
  let details: RegistrationDetails | null = null;
  if (token) {
    try {
      details = await getRegistration(base, token);
    } catch {
      details = null;
    }
  }
  fillTree(root, tagValues(details, base, token));
  window.FFS = { ...(window.FFS ?? {}), registration: details };
  root.setAttribute('data-ffs-ready', '');
  root.style.visibility = '';
  document.dispatchEvent(
    new CustomEvent('ffs:confirmation', { detail: { registration: details } }),
  );
}
